/**
 * LabelRenderer - Universal Label Rendering System
 *
 * Flexible renderer for chakra position labels with different visual styles.
 * Supports multiple label types (planets, muhurta, custom markers) with
 * configurable appearance.
 */

import { devError } from '../../../../../utils/devLog';
import * as d3 from 'd3';
import { ChakraLabel, LabelStyle, LabelRenderOptions, LabelRenderResult } from '../types/LabelTypes';

export class LabelRenderer {
    /**
     * Render a single label with specified style
     */
    public static renderLabel(options: LabelRenderOptions): LabelRenderResult {
        const { parentGroup, label, style, centerPosition, labelPosition, classPrefix = 'chakra-label' } = options;

        const result: LabelRenderResult = {
            success: false,
            elements: {}
        };

        try {
            const groupNode = parentGroup.node();
            if (!groupNode) {
                throw new Error('Parent group node not found');
            }

            // 1. Render line from center to label (if configured)
            if (style.line.strokeWidth > 0) {
                const lineElement = this.renderLine(centerPosition, labelPosition, style, classPrefix, label.name);
                groupNode.appendChild(lineElement);
                result.elements.line = lineElement;
            }

            // 2. Render background shape (if not "none")
            if (style.background.shape !== 'none') {
                const backgroundElement = this.renderBackground(labelPosition, style, classPrefix, label.name);
                groupNode.appendChild(backgroundElement);
                result.elements.background = backgroundElement;
            }

            // 3. Render text label
            const textElement = this.renderText(labelPosition, label, style, classPrefix, options);
            groupNode.appendChild(textElement);
            result.elements.text = textElement;

            result.success = true;
        } catch (error) {
            result.error = error instanceof Error ? error.message : 'Unknown error';
            devError(`[LabelRenderer] Failed to render label "${label.name}":`, error);
        }

        return result;
    }

    /**
     * Render line from center to label position
     */
    private static renderLine(
        centerPosition: { x: number; y: number },
        labelPosition: { x: number; y: number },
        style: LabelStyle,
        classPrefix: string,
        labelName: string
    ): SVGLineElement {
        const lineElement = document.createElementNS('http://www.w3.org/2000/svg', 'line');

        lineElement.setAttribute('x1', centerPosition.x.toString());
        lineElement.setAttribute('y1', centerPosition.y.toString());
        lineElement.setAttribute('x2', labelPosition.x.toString());
        lineElement.setAttribute('y2', labelPosition.y.toString());
        lineElement.setAttribute('stroke', style.line.stroke);
        lineElement.setAttribute('stroke-width', style.line.strokeWidth.toString());

        if (style.line.opacity !== undefined) {
            lineElement.setAttribute('opacity', style.line.opacity.toString());
        }

        lineElement.setAttribute('class', `${classPrefix}-line ${labelName.toLowerCase()}`);

        return lineElement;
    }

    /**
     * Render background shape (square or circle)
     */
    private static renderBackground(
        labelPosition: { x: number; y: number },
        style: LabelStyle,
        classPrefix: string,
        labelName: string
    ): SVGElement {
        const { background } = style;
        let backgroundElement: SVGElement;

        if (background.shape === 'circle') {
            // Render circle
            backgroundElement = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            backgroundElement.setAttribute('cx', labelPosition.x.toString());
            backgroundElement.setAttribute('cy', labelPosition.y.toString());
            backgroundElement.setAttribute('r', (background.size / 2).toString());
        } else {
            // Render square (default)
            backgroundElement = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            backgroundElement.setAttribute('x', (labelPosition.x - background.size / 2).toString());
            backgroundElement.setAttribute('y', (labelPosition.y - background.size / 2).toString());
            backgroundElement.setAttribute('width', background.size.toString());
            backgroundElement.setAttribute('height', background.size.toString());
        }

        backgroundElement.setAttribute('fill', background.fill);
        backgroundElement.setAttribute('stroke', background.stroke);
        backgroundElement.setAttribute('stroke-width', background.strokeWidth.toString());

        if (background.opacity !== undefined) {
            backgroundElement.setAttribute('opacity', background.opacity.toString());
        }

        backgroundElement.setAttribute('class', `${classPrefix}-background ${labelName.toLowerCase()}`);

        return backgroundElement;
    }

    /**
     * Render text label
     */
    private static renderText(
        labelPosition: { x: number; y: number },
        label: ChakraLabel,
        style: LabelStyle,
        classPrefix: string,
        options: LabelRenderOptions
    ): SVGTextElement {
        const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');

        textElement.setAttribute('x', labelPosition.x.toString());
        textElement.setAttribute('y', labelPosition.y.toString());
        textElement.setAttribute('text-anchor', 'middle');
        textElement.setAttribute('dominant-baseline', 'central');
        textElement.setAttribute('font-family', style.text.fontFamily);
        textElement.setAttribute('font-size', style.text.fontSize.toString());
        textElement.setAttribute('font-weight', style.text.fontWeight);

        // Use custom color if provided, otherwise use style color
        const textFill = label.color || style.text.fill;
        textElement.setAttribute('fill', textFill);

        textElement.setAttribute('stroke', style.text.stroke);
        textElement.setAttribute('stroke-width', style.text.strokeWidth.toString());
        textElement.setAttribute('class', `${classPrefix}-text ${label.name.toLowerCase()}`);

        // Add data attributes for debugging and interaction
        textElement.setAttribute('data-longitude', label.longitude.toString());
        textElement.setAttribute('data-container', options.containerIndex.toString());
        textElement.setAttribute('data-local-degree', options.localDegree.toString());
        textElement.setAttribute('data-label-name', label.name);

        textElement.textContent = label.name;

        return textElement;
    }

    /**
     * Render multiple labels at once
     */
    public static renderLabels(
        labels: ChakraLabel[],
        parentGroup: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
        getStyle: (label: ChakraLabel) => LabelStyle,
        calculatePosition: (label: ChakraLabel) => {
            containerIndex: number;
            localDegree: number;
            centerPosition: { x: number; y: number };
            labelPosition: { x: number; y: number };
        } | null
    ): LabelRenderResult[] {
        return labels.map(label => {
            const positions = calculatePosition(label);

            if (!positions) {
                return {
                    success: false,
                    elements: {},
                    error: `Failed to calculate position for label "${label.name}"`
                };
            }

            return this.renderLabel({
                parentGroup,
                label,
                style: getStyle(label),
                containerIndex: positions.containerIndex,
                localDegree: positions.localDegree,
                centerPosition: positions.centerPosition,
                labelPosition: positions.labelPosition
            });
        });
    }
}
